<template>
  <v-container class="dashboard-container pa-0" app fluid v-if="authenticated">
    <SlideAside v-model="logVisible" :width="500">
      <!-- main app -->
      <template #default>
        <v-row density="compact" class="ma-0 pa-0 fill-height dashboard-main-row">
          <v-col cols="12" class="ma-0 py-0 pl-0 pr-4 fill-height dashboard-main-col">
            <!-- Simple mode: panes are ordered to match the simple-mode tab order so the v-window
                 slide direction is correct and swiping never lands on a hidden (technical) pane. -->
            <v-window v-if="userMode === 'simple'" v-model="tab" class="tab-windows fill-height">
              <v-window-item value="assistants" class="mr-4 ml-8 mt-4">
                <AssistantsView />
              </v-window-item>
              <v-window-item value="automations" class="mr-4 ml-8 mt-4">
                <AutomationsView />
              </v-window-item>
              <v-window-item value="chat" class="my-0 mt-4 fill-height">
                <DashboardChat />
              </v-window-item>
              <v-window-item value="providers" class="mr-4 ml-8 mt-4 fill-height">
                <Providers />
              </v-window-item>
              <v-window-item value="mcp" class="mr-4 ml-8 mt-4">
                <Mcp />
              </v-window-item>
            </v-window>

            <!-- Advanced / unset: full pane set in the default order. -->
            <v-window v-else v-model="tab" class="tab-windows fill-height">
              <v-window-item value="providers" class="mr-4 ml-8 mt-4 fill-height">
                <Providers />
              </v-window-item>
              <v-window-item value="models" class="mr-4 ml-8 mt-4">
                <Models />
              </v-window-item>
              <v-window-item value="agents" class="mr-4 ml-8 mt-4">
                <Agents />
              </v-window-item>
              <!-- Routed panes: each router-view is gated by its tab so the two routed panes never
                   render the same matched child component twice (v-window keeps visited panes
                   mounted, and every ungated router-view renders the active child route). -->
              <v-window-item value="workflows" class="mr-4 mt-4 fill-height dashboard-workflows-pane">
                <router-view v-if="tab === 'workflows'" /> <!-- Use router-view for the child route -->
              </v-window-item>
              <v-window-item value="factories" class="mr-4 mt-4 fill-height dashboard-workflows-pane">
                <Factories />
              </v-window-item>
              <v-window-item value="pages" class="mr-4 ml-8 mt-4">
                <router-view v-if="tab === 'pages'" />
              </v-window-item>
              <v-window-item value="schedules" class="mr-4 ml-8 mt-4 fill-height">
                <Schedules />
              </v-window-item>
              <v-window-item value="embeddings" class="mr-4 ml-8 mt-4">
                <Embeddings />
              </v-window-item>
              <v-window-item value="studio" class="my-4 mt-4">
                <PromptStudio />
              </v-window-item>
              <v-window-item value="chat" class="my-0 mt-4 fill-height">
                <DashboardChat />
              </v-window-item>
              <v-window-item value="mcp" class="mr-4 ml-8 mt-4">
                <Mcp />
              </v-window-item>
            </v-window>
          </v-col>
        </v-row>
      </template>

      <!-- logs -->
      <template #right>
        <div class="log-container fill-height overflow-y-auto pb-8">
          <header class="log-header text-h6 px-4 pt-4 pb-0">Event log</header>

          <v-list density="compact" class="log-list">
            <v-list-item v-for="e in events" :key="e.id" class="log-item">
              <v-list-item-title class="log-title">
                {{ e.title }}
              </v-list-item-title>

              <v-list-item-content v-if="e.content != null" class="log-text">
                <span v-if="typeof e.content === 'string'">
                  {{ e.content }}
                </span>
                <vue-json-pretty v-else :data="e.content" deep="2" theme="dark" />
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <span id="bottom"></span>
        </div>
      </template>
    </SlideAside>
    <AgentWizardDialog />
    <ConfigurationChatDialog />
    <UserModeDialog :ready="initialDataLoaded" />
    <SimpleUiAnnouncementDialog :eligible="announceSimpleUi" />
  </v-container>
</template>
<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import Models from '@/components/Models.vue'
import Agents from '@/components/Agents.vue'
import AutomationsView from '@/views/AutomationsView.vue'
import AssistantsView from '@/views/AssistantsView.vue'
import Embeddings from '@/components/Embeddings.vue'
import Providers from '@/components/Providers.vue'
import PromptStudio from '@/components/PromptStudio.vue'
import Mcp from '@/components/Mcp.vue'
import Schedules from '@/components/Schedules.vue'
import Factories from '@/views/Factories.vue'
import SlideAside from '@/components/SlideAside.vue'
import AgentWizardDialog from '@/components/dialogs/AgentWizardDialog.vue'
import ConfigurationChatDialog from '@/components/dialogs/ConfigurationChatDialog.vue'
import UserModeDialog from '@/components/dialogs/UserModeDialog.vue'
import SimpleUiAnnouncementDialog from '@/components/dialogs/SimpleUiAnnouncementDialog.vue'
import DashboardChat from '@/components/DashboardChat.vue'
import { mapGetters } from 'vuex'
import { LogEvent } from '@/store/global'
import { UserAgentConfigs } from '@/types/agents'
import type { ProviderConfig } from '@/types/providers'
import { connectedProviderSet } from '@/utils/assistants'
import { getConfigAgentStatus } from '@/utils/api'
import { getDashboardTabFromPath, getDashboardTabPath, isTabVisibleForMode, toDashboardTabValue, type UserMode } from '@/router/dashboardTabs'
import { preloadVoiceModel } from '@/composables/useVoiceInput'

@Options({
  components: {
    Models,
    Agents,
    AutomationsView,
    AssistantsView,
    Embeddings,
    Providers,
    PromptStudio,
    Mcp,
    Schedules,
    Factories,
    SlideAside,
    AgentWizardDialog,
    ConfigurationChatDialog,
    UserModeDialog,
    SimpleUiAnnouncementDialog,
    DashboardChat
  },
  computed: {
    ...mapGetters(['authenticated', 'selectedTab', 'showLog', 'logEvents', 'agentConfigs', 'userMode', 'providerConfigsList', 'awaitingModeChoice', 'shouldAnnounceSimpleUi'])
  },
  watch: {
    '$route.path': {
      handler() {
        this.syncTabFromRoute()
      },
      immediate: true
    },
    selectedTab(value: string) {
      if (value === 'schedules') {
        void this.$store.dispatch('fetchScheduledRuns')
      }
      this.enforceVisibleTab()
    },
    userMode() {
      this.enforceVisibleTab()
    }
  }
})
export default class DashboardView extends mixins(AnalyticsMixin) {
  public authenticated!: boolean | null
  public selectedTab!: string
  public showLog!: boolean
  public logEvents!: Array<LogEvent>
  public agentConfigs!: UserAgentConfigs
  public userMode!: UserMode | null
  public providerConfigsList!: ProviderConfig[]
  public awaitingModeChoice!: boolean
  public shouldAnnounceSimpleUi!: boolean
  // Initial fetchAllData() has resolved — gates the UserModeDialog so it never fires on stale data.
  initialDataLoaded = false
  // Snapshot of shouldAnnounceSimpleUi, captured once after the initial fetch (and after onboarding
  // state is settled in mounted). Drives the one-time legacy-user announcement; snapshotting keeps it a
  // load-time decision so growing past 2 agents mid-session can't pop it.
  announceSimpleUi = false
  
  // Computed property for tab
  get tab() {
    return this.selectedTab ?? ''
  }
  
  set tab(value: string) {
    this.navigateToDashboardTab(value)
  }

  get logVisible() {
    return this.showLog ?? false
  }

  set logVisible(value: boolean) {
    this.$store.dispatch('toggleLog')
  }

  get events() {
    return this.logEvents.reverse() ?? []
  }

  format(obj: Object | string) {
    if (typeof obj === 'string') {
      return obj
    }
    return JSON.stringify(obj)
  }

  async mounted() {
    this.trackComponentView('dashboard_view', {
      selected_tab: this.selectedTab
    })
    // Warm the browser-local voice model so the mic is instant later in the session (the SPA keeps
    // it in memory across route changes). Guarded and idle-deferred internally; failures are silent.
    preloadVoiceModel()
    await this.fetchAllData()
    this.initialDataLoaded = true
    // First-time detection: only a brand-new account reaches the dashboard with no mode chosen AND no
    // providers (legacy users always have at least one). Latch it (persisted) so the mode prompt fires
    // once the first provider connects — even across a refresh mid-onboarding. setUserMode clears it.
    if (this.userMode == null && connectedProviderSet(this.providerConfigsList || []).size === 0) {
      this.$store.dispatch('setModeChoicePending', true)
    }
    const configStatus = await getConfigAgentStatus()
    if (this.awaitingModeChoice) {
      // Mid-onboarding (provider connected, mode not picked — e.g. after a refresh): the mode dialog
      // owns the screen. UserModeDialog reopens the config chat once a mode is chosen.
    } else if (configStatus && !configStatus.provisioned) {
      if (configStatus.availableProviders && configStatus.availableProviders.length > 0) {
        // Existing user with providers but no config agent — show model selection
        this.$store.dispatch('toggleConfigChat', true)
      } else if (Object.keys(this.agentConfigs).length === 0) {
        // New user with no providers — show provider setup
        this.$store.dispatch('toggleConfigChat', true)
      }
    } else if (Object.keys(this.agentConfigs).length === 0 && this.userMode === 'advanced') {
      // Auto-open the advanced wizard only for users who explicitly chose advanced. With no mode
      // chosen the UserModeDialog owns onboarding, and simple users have the Assistants surface.
      this.$store.dispatch('toggleAgentWizard', true)
    }
    // Snapshot eligibility for the one-time Simple-mode announcement now that data is fetched and any
    // modeChoicePending latch above has been applied. Captured once so it stays a load-time decision.
    this.announceSimpleUi = this.shouldAnnounceSimpleUi
  }

  async fetchAllData() {
    await Promise.all([
      this.$store.dispatch('fetchProviders'),
      this.$store.dispatch('fetchModels'),
      this.$store.dispatch('fetchModelConfigs'),
      this.$store.dispatch('fetchMcpServers'),
      this.$store.dispatch('fetchScheduledRuns')
    ])
    await Promise.all([
      this.$store.dispatch('fetchPublishedAgents'),
      this.$store.dispatch('fetchSharedAgents')
    ])
  }

  syncTabFromRoute(): void {
    const routeTab = getDashboardTabFromPath(this.$route.path)
    if (routeTab != null && routeTab !== this.selectedTab) {
      this.$store.dispatch('setTab', routeTab)
    }
    this.enforceVisibleTab()
  }

  // In simple mode, keep the user off tabs they can't see (e.g. a stale selection or a deep link).
  enforceVisibleTab(): void {
    const tab = toDashboardTabValue(this.selectedTab)
    if (tab != null && !isTabVisibleForMode(tab, this.userMode)) {
      // Redirect to a tab that exists in the current mode's window: Assistants is the simple-mode
      // home; Agents is its advanced equivalent (also valid when no mode is chosen yet). This avoids
      // deep links to /dashboard/assistants|automations rendering a blank pane in the advanced window.
      this.navigateToDashboardTab(this.userMode === 'simple' ? 'assistants' : 'agents')
    }
  }

  navigateToDashboardTab(value: string): void {
    const tabValue = toDashboardTabValue(value)
    if (tabValue == null) {
      return
    }
    if (tabValue !== this.selectedTab) {
      this.$store.dispatch('setTab', tabValue)
    }
    const path = getDashboardTabPath(tabValue)
    if (this.$route.path !== path) {
      this.$router.push(path)
    }
  }
}
</script>
<style lang="scss">
.dashboard-container {
  height: calc(100vh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px));
  max-height: calc(100vh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px));
  min-height: 0;
  overflow: hidden;
}

.dashboard-main-row {
  min-height: 0;
}

.dashboard-main-col {
  min-height: 0;
  overflow: hidden;
}

.tab-windows {
  min-height: 0;
  overflow: hidden;
}

.tab-windows > .v-window__container {
  height: 100%;
  min-height: 0;
}

.tab-windows > .v-window__container > .v-window-item:not(.dashboard-workflows-pane) {
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.dashboard-workflows-pane {
  min-height: 0;
  overflow: hidden;
}

.dashboard-workflows-pane > * {
  height: 100%;
  min-height: 0;
}
</style>
